import { FC, Suspense, useCallback } from 'react';
import { Navigate, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, Box, Stack, Typography } from '@mui/material';

import { useRouter } from '@cvt/routing';
import { BodyLoading } from '@cvt/components/layout/BodyLoading';

import { cacheKeys as authCacheKeys } from '@modules/Auth/config';

import { useFeedbackSession } from '../hooks/useFeedbackSession';
import { FeedbackSessionSwipableComponent } from '../components/FeedbackSessionSwipableComponent';


export const FeedbackSession: FC = () => {
  const router = useRouter();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { session, isLoading, isError } = useFeedbackSession();

  const onComplete = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [authCacheKeys.getMe] });
    navigate(router.home.path, { replace: true });
  }, [navigate, queryClient, router.home.path]);

  if (isLoading) {
    return <BodyLoading height="100%" />;
  }

  if (isError) {
    return (
      <Stack p={3} alignItems="center">
        <Alert severity="error">
          We couldn&apos;t load your feedback session. Please try again later.
        </Alert>
      </Stack>
    );
  }

  if (!session) {
    return <Navigate to={router.home.path} replace />;
  }

  return (
    <Box display="flex" flexDirection="column">
      <Stack p={3} alignItems="center">
        <Typography variant="h5" align="center">
          {session.label || 'Feedback Check-in'}
        </Typography>
      </Stack>
      <Suspense fallback={<BodyLoading height="100%" />}>
        <FeedbackSessionSwipableComponent session={session} onComplete={onComplete} />
      </Suspense>
    </Box>
  );
};
